import type Client from "@/models/Client";
import type User from "@/models/user/User";
import type Deal from "@/models/Deal";
import type { DealCreationMode } from "@/models/Deal";
import type { ProductType } from "@/commons/enums/ProductType";

/** V3 → V2: cart iframe messages (see lendi Cart modal send sites). */
export type CartGoToClientDealsPayload = {
  clientId: string | number;
};

export type CartAddNewDealPayload = {
  clientId: string | number;
  mode?: DealCreationMode;
};

export type CartEditClientDataPayload = {
  isCompany: boolean;
  productType?: string | null;
  mode?: DealCreationMode;
  editingFields?: (keyof Client)[];
};

export type BridgeClientPayload = {
  id?: string | number;
  isCompany?: boolean;
  email?: string | null;
  phone?: string | null;
  [key: string]: unknown;
};

export type ChangeOwnerPayload = {
  client: Client;
  owner: User | null;
};

export type NewDealAddNewClientPayload = {
  isCompany: boolean;
  productType?: ProductType | null;
  mode?: DealCreationMode;
};

export type SaveOnQuickDealPayload = {
  dealId: number;
  /** Comparator properties saved earlier in the modal — fincrm closes it after this message, so they travel here. */
  savedRealEstateOfferIds?: string[];
};

export type NewDealWithClientCreationAddNewClientPayload =
  NewDealAddNewClientPayload;

export type NewDealWithClientCreatedPayload = {
  client: Client | null;
};

export type GoToClientDetailsPayload = {
  client: Client | null;
};

export type BackToClientFormPayload = {
  client: Client | null;
  mode?: DealCreationMode;
};

/** V2 → V3: continue the open wizard as the client-creation variant, in place. */
export type ResumeAfterClientCreatedPayload = {
  goToCart?: boolean;
};

/** V3 → V2 control: what this child build supports. Absent on older builds. */
export type ReadyPayload = {
  capabilities?: {
    inPlaceClientCreate?: boolean;
  };
};

export type NewClientModalClosePayload = BridgeClientPayload & {
  mode?: DealCreationMode;
};

export type NewDealModalClosePayload = {
  mode?: DealCreationMode;
  clientId?: string | number;
  dealId?: string | number;
  draftDeals?: Array<{ id: string | number }>;
  deal?: Deal;
  /** Comparator properties the modal put on a deal or into a draft — the comparator lets go of them. */
  savedRealEstateOfferIds?: string[];
  [key: string]: unknown;
};

export type AnotherClientCreatedPayload = NewClientModalClosePayload;
export type DealCreatedPayload = NewDealModalClosePayload | null;
export type ClientDataEditedPayload = NewClientModalClosePayload | null;

/** V2 → V3 control: which V3 route/modal to mount inside the preloaded iframe. */
export type OpenModalPayload = {
  route: string;
  query?: Record<string, unknown>;
  params?: Record<string, unknown>;
};

/** V3 → V2 control: host confirms it navigated to `route`. */
export type NavigatedPayload = {
  route: string;
};

export const IFRAME_MESSAGE_TYPES = {
  NEW_CLIENT_MODAL: "NEW_CLIENT_MODAL",
  NEW_DEAL_MODAL: "NEW_DEAL_MODAL",
  NEW_DEAL_WITH_CLIENT_CREATION_MODAL: "NEW_DEAL_WITH_CLIENT_CREATION_MODAL",
  CLIENT_AGREEMENTS_MODAL: "CLIENT_AGREEMENTS_MODAL",
  IFRAME_RESIZE: "IFRAME_RESIZE",
  DELEGATE_DEAL_MODAL: "DELEGATE_DEAL_MODAL",
  CART_MODAL: "CART_MODAL",
  DIVISION_MEMBERS: "DIVISION_MEMBERS",
  MODAL_TEST_MESSAGE: "MODAL_TEST_MESSAGE",
  /** Lendi AI iframe ↔ FinCRM parent (voice, etc.) */
  LENDI_AI_IFRAME: "LENDI_AI_IFRAME",
  IFRAME_COMMAND: "IFRAME_COMMAND",
  IFRAME_STATUS: "IFRAME_STATUS",
} as const;

export type IframeMessageType =
  (typeof IFRAME_MESSAGE_TYPES)[keyof typeof IFRAME_MESSAGE_TYPES];

export type IframeModalType =
  | typeof IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL
  | typeof IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL
  | typeof IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL
  | typeof IFRAME_MESSAGE_TYPES.CLIENT_AGREEMENTS_MODAL
  | typeof IFRAME_MESSAGE_TYPES.DELEGATE_DEAL_MODAL
  | typeof IFRAME_MESSAGE_TYPES.CART_MODAL;

export const IFRAME_ACTIONS = {
  CLOSE_MODAL: "CLOSE_MODAL",
  FULLSCREEN_OPEN: "FULLSCREEN_OPEN",
  FULLSCREEN_CLOSE: "FULLSCREEN_CLOSE",
  GO_TO_CLIENT: "GO_TO_CLIENT",
  CHANGE_OWNER: "CHANGE_OWNER",
  ANOTHER_CLIENT_CREATED: "ANOTHER_CLIENT_CREATED",
  RESUME_AFTER_CLIENT_CREATED: "RESUME_AFTER_CLIENT_CREATED",
  CLIENT_CREATED: "CLIENT_CREATED",
  ANOTHER_CLIENT_ADDED: "ANOTHER_CLIENT_ADDED",
  ADD_NEW_CLIENT: "ADD_NEW_CLIENT",
  GO_TO_CLIENT_DETAILS: "GO_TO_CLIENT_DETAILS",
  BACK_TO_CLIENT_FORM: "BACK_TO_CLIENT_FORM",
  ADD_NEW_DEAL: "ADD_NEW_DEAL",
  GO_TO_CLIENT_DEALS: "GO_TO_CLIENT_DEALS",
  DEAL_CREATED: "DEAL_CREATED",
  EDIT_CLIENT_DATA: "EDIT_CLIENT_DATA",
  CLIENT_DATA_EDITED: "CLIENT_DATA_EDITED",
  DIVISION_MEMBERS_UPDATED: "DIVISION_MEMBERS_UPDATED",
  DIVISION_MEMBERS_UPDATING: "DIVISION_MEMBERS_UPDATING",
  ASK_MICROPHONE_PERMISSION: "ASK_MICROPHONE_PERMISSION",
  MICROPHONE_PERMISSION_GRANTED: "MICROPHONE_PERMISSION_GRANTED",
  SAVE_ON_QUICK_DEAL: "SAVE_ON_QUICK_DEAL",
  SELECT_EXISTING_CLIENT: "SELECT_EXISTING_CLIENT",
  OPEN_MODAL: "OPEN_MODAL",
  RESET: "RESET",
  PING: "PING",
  READY: "READY",
  NAVIGATED: "NAVIGATED",
  CONTENT_READY: "CONTENT_READY",
} as const;

export type IframeAction = (typeof IFRAME_ACTIONS)[keyof typeof IFRAME_ACTIONS];

export type IframeBridgeMessage<T = undefined> = {
  type: IframeMessageType;
  action: IframeAction;
  data?: T;
};

type BridgeMessageLike =
  | {
    type?: unknown;
    action?: unknown;
  }
  | null
  | undefined;

export type EventArgs<T> = [T] extends [undefined]
  ? []
  : undefined extends T
  ? [data?: T]
  : [data: T];

/**
 * Shared postMessage contract used by fincrm (parent) and lendi-crm (iframe).
 * Every bridge message on the wire is `{ type, action, data? }`, where `type`
 * selects the modal/channel and `action` selects the intent within it.
 */
export type IframeBridgeEvent<T = undefined> = {
  type: IframeMessageType;
  action: IframeAction;
  create: (...args: EventArgs<T>) => IframeBridgeMessage<T>;
  matches: (message: BridgeMessageLike) => message is IframeBridgeMessage<T>;
};

export type PayloadOf<E extends IframeBridgeEvent<any>> =
  E extends IframeBridgeEvent<infer T> ? T : never;

export type EventArgsOf<E extends IframeBridgeEvent<any>> = EventArgs<
  PayloadOf<E>
>;

/**
 * `create()` builds the outbound wire payload, while `matches()` is the
 * runtime guard listeners use before reading `message.data`.
 */
const defineBridgeEvent = <T = undefined>(
  type: IframeMessageType,
  action: IframeAction
): IframeBridgeEvent<T> => ({
  type,
  action,
  create: (...args: EventArgs<T>) => {
    const data = args[0] as T | undefined;
    return (
      typeof data === "undefined" ? { type, action } : { type, action, data }
    ) as IframeBridgeMessage<T>;
  },
  matches: (message: BridgeMessageLike): message is IframeBridgeMessage<T> =>
    message?.type === type && message?.action === action,
});

const defineModalCloseEvent = <T = undefined>(
  type: IframeModalType
): IframeBridgeEvent<T> =>
  defineBridgeEvent<T>(type, IFRAME_ACTIONS.CLOSE_MODAL);

export function isIframeModalType(type: string): type is IframeModalType {
  return Object.prototype.hasOwnProperty.call(modalCloseEventByType, type);
}

export type ModalClosePayloadByType = {
  [IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL]:
  | NewClientModalClosePayload
  | null
  | undefined;
  [IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL]:
  | NewDealModalClosePayload
  | null
  | undefined;
  [IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL]:
  | NewDealModalClosePayload
  | null
  | undefined;
  [IFRAME_MESSAGE_TYPES.CLIENT_AGREEMENTS_MODAL]: boolean | null | undefined;
  [IFRAME_MESSAGE_TYPES.DELEGATE_DEAL_MODAL]:
  | string
  | number
  | null
  | undefined;
  [IFRAME_MESSAGE_TYPES.CART_MODAL]: boolean | null | undefined;
};

type TypedModalCloseEventMap = {
  [K in IframeModalType]: IframeBridgeEvent<ModalClosePayloadByType[K]>;
};

/**
 * Names are based on the receiver, not the current file.
 * In fincrm, `toFincrm` describes messages expected from the lendi iframe,
 * while `toLendiCrm` describes messages fincrm sends into the CRM `iframe.contentWindow`.
 * `toLendiAi` describes messages fincrm sends into the **Lendi AI** iframe (e.g. mic grant).
 * `infra` contains transport-level messages reused on both sides.
 */
export const iframeBridge = {
  toFincrm: {
    newClient: {
      goToClient: defineBridgeEvent<number | null>(
        IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL,
        IFRAME_ACTIONS.GO_TO_CLIENT
      ),
      changeOwner: defineBridgeEvent<ChangeOwnerPayload>(
        IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL,
        IFRAME_ACTIONS.CHANGE_OWNER
      ),
    },
    newDeal: {
      addNewClient: defineBridgeEvent<NewDealAddNewClientPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL,
        IFRAME_ACTIONS.ADD_NEW_CLIENT
      ),
      saveOnQuickDeal: defineBridgeEvent<SaveOnQuickDealPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL,
        IFRAME_ACTIONS.SAVE_ON_QUICK_DEAL
      ),
    },
    newDealWithClientCreation: {
      addNewClient:
        defineBridgeEvent<NewDealWithClientCreationAddNewClientPayload>(
          IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
          IFRAME_ACTIONS.ADD_NEW_CLIENT
        ),
      clientCreated: defineBridgeEvent<NewDealWithClientCreatedPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        IFRAME_ACTIONS.CLIENT_CREATED
      ),
      anotherClientAdded: defineBridgeEvent<NewDealWithClientCreatedPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        IFRAME_ACTIONS.ANOTHER_CLIENT_ADDED
      ),
      goToClientDetails: defineBridgeEvent<GoToClientDetailsPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        IFRAME_ACTIONS.GO_TO_CLIENT_DETAILS
      ),
      backToClientForm: defineBridgeEvent<BackToClientFormPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        IFRAME_ACTIONS.BACK_TO_CLIENT_FORM
      ),
    },
    cart: {
      addNewDeal: defineBridgeEvent<CartAddNewDealPayload>(
        IFRAME_MESSAGE_TYPES.CART_MODAL,
        IFRAME_ACTIONS.ADD_NEW_DEAL
      ),
      goToClientDeals: defineBridgeEvent<CartGoToClientDealsPayload>(
        IFRAME_MESSAGE_TYPES.CART_MODAL,
        IFRAME_ACTIONS.GO_TO_CLIENT_DEALS
      ),
      editClientData: defineBridgeEvent<CartEditClientDataPayload>(
        IFRAME_MESSAGE_TYPES.CART_MODAL,
        IFRAME_ACTIONS.EDIT_CLIENT_DATA
      ),
    },
    divisionMembers: {
      updating: defineBridgeEvent<string>(
        IFRAME_MESSAGE_TYPES.DIVISION_MEMBERS,
        IFRAME_ACTIONS.DIVISION_MEMBERS_UPDATING
      ),
      updated: defineBridgeEvent<string>(
        IFRAME_MESSAGE_TYPES.DIVISION_MEMBERS,
        IFRAME_ACTIONS.DIVISION_MEMBERS_UPDATED
      ),
    },
    modalTest: {
      message: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.MODAL_TEST_MESSAGE,
        IFRAME_ACTIONS.CLOSE_MODAL
      ),
    },
    //TODO: in case of more events create toFincrmFromLendiCrm and toFincrmFromLendiAi....
    ai: {
      askMicrophone: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.LENDI_AI_IFRAME,
        IFRAME_ACTIONS.ASK_MICROPHONE_PERMISSION
      ),
    },
    control: {
      ready: defineBridgeEvent<ReadyPayload | undefined>(
        IFRAME_MESSAGE_TYPES.IFRAME_STATUS,
        IFRAME_ACTIONS.READY
      ),
      navigated: defineBridgeEvent<NavigatedPayload>(
        IFRAME_MESSAGE_TYPES.IFRAME_STATUS,
        IFRAME_ACTIONS.NAVIGATED
      ),
      // Sent by V3 on page:finish of a modal route — content actually painted.
      // NAVIGATED only means the route was confirmed (~275 ms in).
      contentReady: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.IFRAME_STATUS,
        IFRAME_ACTIONS.CONTENT_READY
      ),
    },
  },
  toLendiCrm: {
    newDeal: {
      // Parent selects an existing client (picked from the duplicate suggestion)
      // straight into the open deal wizard — same effect as picking from the list.
      existingClientSelected: defineBridgeEvent<NewClientModalClosePayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL,
        IFRAME_ACTIONS.SELECT_EXISTING_CLIENT
      ),
    },
    newDealWithClientCreation: {
      anotherClientCreated: defineBridgeEvent<AnotherClientCreatedPayload>(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        IFRAME_ACTIONS.ANOTHER_CLIENT_CREATED
      ),
      // The created client itself travels the `createdClient` cookie; this
      // payload only carries what the wizard cannot know on its own.
      resumeAfterClientCreated:
        defineBridgeEvent<ResumeAfterClientCreatedPayload>(
          IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
          IFRAME_ACTIONS.RESUME_AFTER_CLIENT_CREATED
        ),
    },
    cart: {
      dealCreated: defineBridgeEvent<DealCreatedPayload>(
        IFRAME_MESSAGE_TYPES.CART_MODAL,
        IFRAME_ACTIONS.DEAL_CREATED
      ),
      clientDataEdited: defineBridgeEvent<ClientDataEditedPayload>(
        IFRAME_MESSAGE_TYPES.CART_MODAL,
        IFRAME_ACTIONS.CLIENT_DATA_EDITED
      ),
    },
    control: {
      openModal: defineBridgeEvent<OpenModalPayload>(
        IFRAME_MESSAGE_TYPES.IFRAME_COMMAND,
        IFRAME_ACTIONS.OPEN_MODAL
      ),
      reset: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.IFRAME_COMMAND,
        IFRAME_ACTIONS.RESET
      ),
      ping: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.IFRAME_COMMAND,
        IFRAME_ACTIONS.PING
      ),
    },
  },
  toLendiAi: {
    askMicrophone: {
      granted: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.LENDI_AI_IFRAME,
        IFRAME_ACTIONS.MICROPHONE_PERMISSION_GRANTED
      ),
    },
  },
  infra: {
    iframeUi: {
      fullscreenOpen: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.IFRAME_RESIZE,
        IFRAME_ACTIONS.FULLSCREEN_OPEN
      ),
      fullscreenClose: defineBridgeEvent(
        IFRAME_MESSAGE_TYPES.IFRAME_RESIZE,
        IFRAME_ACTIONS.FULLSCREEN_CLOSE
      ),
    },
    modal: {
      close: {
        newClient: defineModalCloseEvent<
          ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL]
        >(IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL),
        newDeal: defineModalCloseEvent<
          ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL]
        >(IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL),
        newDealWithClientCreation: defineModalCloseEvent<
          ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL]
        >(IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL),
        delegateDeal: defineModalCloseEvent<
          ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.DELEGATE_DEAL_MODAL]
        >(IFRAME_MESSAGE_TYPES.DELEGATE_DEAL_MODAL),
        cart: defineModalCloseEvent<
          ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.CART_MODAL]
        >(IFRAME_MESSAGE_TYPES.CART_MODAL),
        clientAgreements: defineModalCloseEvent<
          ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.CLIENT_AGREEMENTS_MODAL]
        >(IFRAME_MESSAGE_TYPES.CLIENT_AGREEMENTS_MODAL),
      },
    },
  },
} as const;

/** Reuses the shared CLOSE_MODAL event family for any typed modal host. */
const modalCloseEventByType: TypedModalCloseEventMap = {
  [IFRAME_MESSAGE_TYPES.NEW_CLIENT_MODAL]:
    iframeBridge.infra.modal.close.newClient,
  [IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL]: iframeBridge.infra.modal.close.newDeal,
  [IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL]:
    iframeBridge.infra.modal.close.newDealWithClientCreation,
  [IFRAME_MESSAGE_TYPES.CLIENT_AGREEMENTS_MODAL]:
    iframeBridge.infra.modal.close.clientAgreements,
  [IFRAME_MESSAGE_TYPES.DELEGATE_DEAL_MODAL]:
    iframeBridge.infra.modal.close.delegateDeal,
  [IFRAME_MESSAGE_TYPES.CART_MODAL]: iframeBridge.infra.modal.close.cart,
};

export function createModalCloseMessage<M extends IframeModalType>(
  modalType: M,
  ...args: EventArgs<ModalClosePayloadByType[M]>
): IframeBridgeMessage<ModalClosePayloadByType[M]> {
  return modalCloseEventByType[modalType].create(...args);
}

export function matchesModalCloseMessage(
  message: BridgeMessageLike,
  modalType: IframeModalType
): boolean {
  return modalCloseEventByType[modalType].matches(message);
}
